/**
 * Pallets (#4815) — turn-based card-shedding game. Rules of record:
 * _docs/Q4/pallets/Pallets-Game-Design-Document.md.
 *
 * The server owns every card. Clients receive the public table plus their own hand;
 * face-down identities are never sent to anyone, including their owner, until revealed.
 */
import { HOLDEM_AI_PERSONAS, type HoldemPersonaId } from './Holdem';

export const PALLETS_MODULE_ID = 'pallets';
export const PALLETS_NAME = 'Pallets';
export const PALLETS_MIN_PLAYERS = 2;
export const PALLETS_MAX_PLAYERS = 6;
export const PALLETS_RESERVE_SIZE = 3;
/** AI opponents fill seats the humans leave free; the host chooses how many between games. */
export const PALLETS_MAX_AI_PLAYERS = 5;
/**
 * AI seats wear the Hold'em cast (#4863): the same characters, listening loops and live heads, so a
 * player meets the same table companions in either game.
 */
export const PALLETS_AI_PERSONAS = HOLDEM_AI_PERSONAS;
export const PALLETS_EVENTS = {
  ACTION: 'pallets:action',
  SNAPSHOT: 'pallets:snapshot',
  STATE: 'pallets:state',
  HAND: 'pallets:hand',
  ERROR: 'pallets:error',
  LEAVE: 'pallets:leave',
} as const;

export type PalletsSuit = 'S' | 'H' | 'D' | 'C';
/** Natural ranks: 2 lowest, 14 (ace) highest. J = 11, Q = 12, K = 13. */
export type PalletsRank = 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14;
export interface PalletsCard {
  /** Stable per physical card ('10H', 'AS', 'JK1'). Only ever sent once the card is visible to the recipient. */
  id: string;
  /** null for a joker. */
  rank: PalletsRank | null;
  suit: PalletsSuit | null;
  joker: boolean;
}
export type PalletsZone = 'hand' | 'faceUp' | 'faceDown';
export type PalletsRankOrder = 'ascending' | 'descending';
export type PalletsRotation = 'clockwise' | 'counterclockwise';
export interface PalletsRules {
  jokers: boolean;
  drinking: boolean;
  /** Optional drinking house rule: the winner names one player for an extra sip. */
  winnersToast: boolean;
}
export const DEFAULT_PALLETS_RULES: PalletsRules = {
  jokers: false,
  drinking: false,
  winnersToast: false,
};

export type PalletsPhase =
  | 'lobby'
  /** Cards dealt; players swap hand ↔ face-up and mark Ready. */
  | 'preparing'
  /** The two of spades is face-down; players holding another visible 2 race to claim the start. */
  | 'claiming'
  | 'playing'
  | 'finished';
export type PalletsOpeningRequirement = 'two-of-spades' | 'any-two' | 'free';
export interface PalletsOpening {
  playerId: string;
  requirement: PalletsOpeningRequirement;
}

export interface PalletsPlayer {
  id: string;
  name: string;
  connected: boolean;
  ready: boolean;
  handCount: number;
  faceUp: PalletsCard[];
  /** Opaque slot ids, one per remaining face-down card. They reveal nothing about the card. */
  faceDownSlots: string[];
  /** A computer opponent the server plays; it never drinks and has no camera. */
  ai?: boolean;
  /** Which cast member an AI seat wears (its character, loops and live head). */
  aiPersona?: HoldemPersonaId;
}

export type PalletsDrinkReason = 'pickup' | 'skip' | 'reverse' | 'toast';
export interface PalletsDrinkPrompt {
  id: string;
  playerId: string;
  reason: PalletsDrinkReason;
  /** The player whose action caused it; for a pickup, null unless sent by a three. */
  byPlayerId: string | null;
  turn: number;
  response: 'sipped' | 'declined' | null;
  /** Players who cheered it — purely social, never verification. */
  cheers: string[];
}

export type PalletsMoveKind = 'play' | 'reveal' | 'reveal-failed' | 'pickup';
export interface PalletsMove {
  turn: number;
  kind: PalletsMoveKind;
  playerId: string;
  /** The card played or revealed; null for a plain pickup. */
  card: PalletsCard | null;
  fromZone: PalletsZone | null;
  /** Next player to receive the pile because of a three. */
  sentToId: string | null;
  blownUp: boolean;
  rankOrderToggled: boolean;
  rotationToggled: boolean;
  skippedId: string | null;
  /** Who took the pile (pickup, failed reveal, or the recipient of a three). */
  pickedUpById: string | null;
  /** Ordinary cards added to that hand; powers went to junk. */
  pickedUpCount: number;
  junkedCount: number;
  nextPlayerId: string | null;
  won: boolean;
}

export interface PalletsState {
  roomId: string;
  gameId: string;
  revision: number;
  /** Monotonic turn counter; turn actions must quote it. */
  turn: number;
  phase: PalletsPhase;
  hostId: string;
  rules: PalletsRules;
  players: PalletsPlayer[];
  spectatorIds: string[];
  dealerId: string | null;
  opening: PalletsOpening | null;
  currentPlayerId: string | null;
  rankOrder: PalletsRankOrder;
  rotation: PalletsRotation;
  /** What the next ordinary card must meet; null on an empty pile or a pile of only 7s/jokers/jacks. */
  effectiveRank: PalletsRank | null;
  pile: PalletsCard[];
  junkCount: number;
  lastMove: PalletsMove | null;
  /** Most recent drink prompts (drinking variant only). */
  drinks: PalletsDrinkPrompt[];
  /** A reversal sip the current player must acknowledge before their cards unlock. */
  sipGate: { promptId: string; playerId: string } | null;
  winnerId: string | null;
  previousWinnerId: string | null;
  toastGiven: boolean;
  paused: boolean;
  lastEvent: string;
  serverNow: number;
}

export interface PalletsHand {
  roomId: string;
  gameId: string;
  revision: number;
  turn: number;
  playerId: string;
  cards: PalletsCard[];
  /** Zone the player may act from right now (only meaningful on their turn). */
  playableZone: PalletsZone | null;
  /** Cards (hand or face-up ids) that are legal to play right now. Empty when it is not their turn. */
  legalCardIds: string[];
  canReveal: boolean;
  mustPickUp: boolean;
  canClaim: boolean;
}

type PalletsActionBase = { roomId: string; gameId: string };
type PalletsTurnBase = PalletsActionBase & { turn: number };
export type PalletsAction =
  | (PalletsActionBase & { type: 'configure'; rules: PalletsRules })
  | (PalletsActionBase & { type: 'deal' | 'claim' | 'end' })
  | (PalletsActionBase & { type: 'ai-players'; count: number })
  | (PalletsActionBase & { type: 'swap'; handCardId: string; faceUpCardId: string })
  | (PalletsActionBase & { type: 'ready'; ready: boolean })
  | (PalletsTurnBase & { type: 'play'; cardId: string })
  | (PalletsTurnBase & { type: 'reveal'; slotId: string })
  | (PalletsTurnBase & { type: 'pickup' })
  | (PalletsActionBase & { type: 'sip'; promptId: string; response: 'sipped' | 'declined' })
  | (PalletsActionBase & { type: 'cheer'; promptId: string })
  | (PalletsActionBase & { type: 'toast'; targetId: string });

export function isPalletsPower(card: Pick<PalletsCard, 'rank' | 'joker'>): boolean {
  return card.joker || card.rank === 3 || card.rank === 7 || card.rank === 10 || card.rank === 11;
}
/** Sevens and jokers neither set the comparison rank nor break a matching run. */
export function isPalletsInvisible(card: Pick<PalletsCard, 'rank' | 'joker'>): boolean {
  return card.joker || card.rank === 7;
}
/**
 * Whether a card becomes the rank to beat. Sevens and jokers are invisible, and a jack only
 * flips the direction: after `5 → J`, play a 5 or lower (playtest 2026-10-21).
 */
export function palletsSetsRank(card: Pick<PalletsCard, 'rank' | 'joker'>): boolean {
  return !isPalletsInvisible(card) && card.rank !== 11;
}
/**
 * Equal-or-better rule (playtest 2026-10-21): powers go on anything; any other card must be
 * equal or higher while ascending, equal or lower while descending. No wrap — an ace is the
 * top, so on an ace only an ace or a power plays.
 */
export function palletsBeats(
  card: Pick<PalletsCard, 'rank' | 'joker'>,
  rank: PalletsRank | null,
  order: PalletsRankOrder
): boolean {
  if (isPalletsPower(card) || rank === null) return true;
  return order === 'ascending' ? card.rank! >= rank : card.rank! <= rank;
}
/** What an ordinary card must be on this pile: "5 or higher", "9 or lower", "A only". */
export function palletsRequirement(rank: PalletsRank, order: PalletsRankOrder): string {
  const label = palletsRankLabel(rank);
  const top = order === 'ascending' ? rank === 14 : rank === 2;
  return top ? `${label} only` : `${label} or ${order === 'ascending' ? 'higher' : 'lower'}`;
}
const RANK_LABELS: Record<number, string> = { 11: 'J', 12: 'Q', 13: 'K', 14: 'A' };
export function palletsRankLabel(rank: PalletsRank): string {
  return RANK_LABELS[rank] ?? String(rank);
}
const SUIT_NAMES: Record<PalletsSuit, string> = {
  S: 'spades',
  H: 'hearts',
  D: 'diamonds',
  C: 'clubs',
};
const RANK_NAMES: Record<number, string> = { 11: 'jack', 12: 'queen', 13: 'king', 14: 'ace' };
/** Accessible card name: "queen of hearts", "7 of clubs", "joker". */
export function palletsCardName(card: PalletsCard): string {
  if (card.joker || card.rank === null || card.suit === null) return 'joker';
  return `${RANK_NAMES[card.rank] ?? card.rank} of ${SUIT_NAMES[card.suit]}`;
}
